/*
  <dv-shipping-protection> — the cart's shipping-protection toggle.

  Protection is a real product: each variant is a price tier, and the tier that
  belongs in the cart is whichever one sits closest to a fixed percentage of the
  products subtotal. This element keeps the cart in step with that rule and with
  the shopper's saved preference, adding, swapping or removing the line as the
  cart changes.

  The preference defaults to on and is remembered across visits, so a shopper
  who switches it off does not get it re-added on their next order.
*/

const DV_PROTECTION_KEY = 'dv_shipping_protection';
const DV_PROTECTION_HANDLE = 'shipping-protection';
/* Above this products subtotal the tiers stop being meaningful. */
const DV_PROTECTION_CEILING = 500000;

/*
  Cart writes run one at a time, queued rather than dropped.

  Two things write to the cart: the shopper's quantity controls and the
  protection re-sync that follows them. An earlier "busy" flag discarded
  whichever arrived second, so a quick second click on minus did nothing. A
  promise chain serialises them instead, so every action still lands.
*/
let dvCartQueue = Promise.resolve();

function dvEnqueue(task) {
  dvCartQueue = dvCartQueue.then(task, task).catch(() => {});
  return dvCartQueue;
}

class DvShippingProtection extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.dataset.variants || '[]');
    } catch {
      this.variants = [];
    }
    if (!this.variants.length) return;

    this.rate = parseFloat(this.dataset.targetRate || '0.04');
    this.toggle = this.querySelector('[data-dv-protect-toggle]');
    this.priceEl = this.querySelector('[data-dv-protect-price]');

    this.toggle?.addEventListener('click', () => {
      this.enabled = !this.enabled;
      try {
        localStorage.setItem(DV_PROTECTION_KEY, this.enabled ? 'on' : 'off');
      } catch {
        // Private browsing — the choice just will not persist.
      }
      this.paintToggle();
      dvSyncProtection();
    });

    let stored = null;
    try {
      stored = localStorage.getItem(DV_PROTECTION_KEY);
    } catch {
      stored = null;
    }
    this.enabled = stored !== 'off';

    this.paintToggle();
    dvSyncProtection();
  }

  /** Variant whose price is nearest the target share of the products subtotal. */
  bestVariant(productsTotal) {
    const target = productsTotal * this.rate;
    const sellable = this.variants.filter((v) => v.available);
    const pool = sellable.length ? sellable : this.variants;
    return pool.reduce((best, v) => (Math.abs(v.price - target) < Math.abs(best.price - target) ? v : best));
  }

  /*
    Reflects the on/off state only. Safe before the cart has been read, which
    matters because the cart page re-renders — and so re-mounts this element —
    after every write.
  */
  paintToggle() {
    this.toggle?.setAttribute('aria-checked', String(this.enabled));
  }

  /*
    Writes the charge. Called only with real cart data: Liquid already printed
    the right figure, so painting a zero here before the cart is known would
    replace a correct price with "$0" on every mount.
  */
  paint(line) {
    this.paintToggle();
    if (!this.priceEl) return;
    const cents = this.enabled && line ? line.final_line_price : 0;
    this.priceEl.textContent = dvFormatMoney(cents);
  }

}

/*
  Brings the protection line back in step with the rest of the cart.

  This lives outside the custom element on purpose. The element is only
  rendered while the cart holds products, so removing the last one unmounts it
  — and if the cleanup lived on the element, the protection line it left behind
  would have nothing to clear it. The cart would stay non-empty, showing a
  subtotal and a checkout button for products that are gone.
*/
function dvSyncProtection() {
  return dvEnqueue(dvReconcileProtection);
}

/* Runs inside a queued turn, so it may write without racing anything else. */
async function dvReconcileProtection() {
  const cart = await fetch(`${window.routes.cart_url}.js`)
    .then((r) => r.json())
    .catch(() => null);
  if (!cart) return;

  const line = cart.items.find((i) => i.handle === DV_PROTECTION_HANDLE);
  const productsTotal = cart.items
    .filter((i) => i.handle !== DV_PROTECTION_HANDLE)
    .reduce((sum, i) => sum + i.final_line_price, 0);

  const element = document.querySelector('dv-shipping-protection');
  // The shown price must always match what the cart is actually charging.
  element?.paint(line);

  // An add-on with nothing left to protect goes, whether or not its toggle is
  // still on screen to ask for it.
  if (!productsTotal || productsTotal > DV_PROTECTION_CEILING) {
    if (line) await dvMutateCart({updates: {[line.key]: 0}});
    return;
  }

  // Past this point the tiers and the shopper's preference are needed, and
  // only the element carries them.
  if (!element?.variants?.length) return;

  if (!element.enabled) {
    if (line) await dvMutateCart({updates: {[line.key]: 0}});
    return;
  }

  const wanted = element.bestVariant(productsTotal);
  if (!line) {
    await dvMutateCart({id: wanted.id, quantity: 1}, true);
    return;
  }
  if (line.variant_id !== wanted.id) {
    // Swap tiers: drop the old line, then add the right one.
    await dvMutateCart({updates: {[line.key]: 0}});
    await dvMutateCart({id: wanted.id, quantity: 1}, true);
  }
}

/* Called only from `dvReconcileProtection`, which already holds the queue. */
async function dvMutateCart(body, isAdd = false) {
  try {
    const url = isAdd ? window.routes.cart_add_url : window.routes.cart_update_url;
    const targets = dvCartSectionTargets();
    const response = await fetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({...body, sections: dvCartSectionsParam(targets)}),
    }).then((r) => r.json());
    dvRenderCartSections(response.sections, targets);
  } catch {
    // Leave the cart as-is; the next open re-syncs.
  }
}

/*
  Money formatting that follows the shop's own format string, including how
  many decimals it asks for — this store is set to `amount_no_decimals`, and
  forcing two here printed "$14.00" beside a Liquid-rendered "$14".
*/
function dvFormatMoney(cents) {
  const format = window.dvMoneyFormat || '${{amount}}';
  const token = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
  const decimals = token.includes('no_decimals') ? 0 : 2;
  const amount = (cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
}

/*
  The cart page's section is `dv-cart`, whose generated id lives on `data-id` —
  asking for the literal name `main-cart-items` would render Dawn's section
  instead and overwrite this design.

  It is swapped whole rather than by its inner `.js-contents`, because removing
  the last product replaces the entire form with the empty state; a target
  inside the form simply would not exist in the new HTML, leaving the old lines
  and subtotal on screen.
*/
function dvCartSectionTargets() {
  const targets = [
    {id: 'cart-drawer', source: '.drawer__inner', target: '#CartDrawer .drawer__inner'},
    {id: 'cart-icon-bubble', source: '.shopify-section', target: '#cart-icon-bubble'},
  ];

  const page = document.getElementById('dv-cart-page');
  if (page?.dataset.id) {
    targets.push({id: page.dataset.id, source: '#dv-cart-page', target: '#dv-cart-page'});
  }
  return targets;
}

function dvCartSectionsParam(targets) {
  return [...new Set(targets.map((t) => t.id))].join(',');
}

/**
 * Swaps in freshly rendered cart sections, so the drawer, the cart page and
 * the header bubble all stay in step after any write.
 */
function dvRenderCartSections(sections, targets) {
  if (!sections) return;

  for (const {id, source, target} of targets) {
    const html = sections[id];
    if (!html) continue;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const from = parsed.querySelector(source);
    const to = document.querySelector(target);
    if (from && to) to.innerHTML = from.innerHTML;
  }
}

if (!customElements.get('dv-shipping-protection')) {
  customElements.define('dv-shipping-protection', DvShippingProtection);
}

/*
  Cart quantity changes, keyed by line item rather than line index.

  Dawn updates a line by its position in the cart. That is fine on its own, but
  the shipping-protection tier is swapped by removing and re-adding a line,
  which renumbers everything — so a quantity request issued around the same
  time can point at the wrong line and comes back 422. The line key is stable
  regardless of ordering, so quantity is driven through it instead.

  It also removes a second Dawn constraint: its `validateQuantity` rejects any
  value below `data-min`, so a quantity of zero never reached the network.
  Here the minus button *is* the remove control at the line's minimum.

  Capture phase, because Dawn binds its own handlers on `<quantity-input>` and
  `<cart-items>` and they must not also fire.
*/
function dvCartItemsFor(el) {
  return el.closest('cart-items') || el.closest('cart-drawer-items');
}

function dvSetLineQuantity(input, quantity) {
  const key = input.dataset.quantityLineKey;
  if (!key) return;
  return dvEnqueue(() => dvWriteLineQuantity(input, key, quantity));
}

async function dvWriteLineQuantity(input, key, quantity) {
  const items = dvCartItemsFor(input);
  items?.classList.add('dv-cart-busy');
  try {
    const targets = dvCartSectionTargets();
    const response = await fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({id: key, quantity, sections: dvCartSectionsParam(targets)}),
    }).then((r) => r.json());
    dvRenderCartSections(response.sections, targets);
  } catch {
    // Leave the row as it was; the next interaction re-reads the cart.
  } finally {
    items?.classList.remove('dv-cart-busy');
  }

  // Re-evaluate the protection tier against the new subtotal — and drop the
  // add-on entirely if that was the last product. Queued after this turn so it
  // never overlaps the write that just happened.
  dvSyncProtection();
}

document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest?.('.quantity__button');
    const input = button?.closest('quantity-input')?.querySelector('.quantity__input');
    if (!input || !dvCartItemsFor(input)) return;

    event.preventDefault();
    event.stopPropagation();

    const current = parseInt(input.value, 10) || 0;
    // Quantity rules can set a minimum above one; removal replaces that step.
    const min = parseInt(input.dataset.min, 10) || 1;
    const step = parseInt(input.step, 10) || 1;

    let next;
    if (button.name === 'plus') {
      next = current + step;
      const max = parseInt(input.max, 10);
      if (Number.isFinite(max) && next > max) return;
    } else {
      next = current <= min ? 0 : current - step;
    }

    input.value = next;
    dvSetLineQuantity(input, next);
  },
  true,
);

/* A typed quantity — including 0 or a cleared field, which removes the line. */
document.addEventListener(
  'change',
  (event) => {
    const input = event.target.closest?.('.quantity__input');
    if (!input || !dvCartItemsFor(input)) return;

    event.stopPropagation();
    const value = Math.max(0, parseInt(input.value, 10) || 0);
    input.value = value;
    dvSetLineQuantity(input, value);
  },
  true,
);

/*
  Closes the drawer from its ✕. Dawn wired this with an inline onclick; a
  delegated listener does the same job without inline script and keeps working
  after the drawer re-renders itself on every cart change.
*/
document.addEventListener('click', (event) => {
  const close = event.target.closest?.('.drawer__close');
  // Dawn's menu drawer reuses this class, so only act inside the cart drawer.
  const drawer = close?.closest('cart-drawer');
  if (!drawer) return;
  event.preventDefault();
  drawer.close();
});

/*
  Clears a cart left holding protection and nothing else — a tab closed between
  the write that removes the last product and the one that removes its add-on.
  Liquid marks that state on the empty drawer, so an ordinary page pays nothing
  for the check.
*/
if (document.querySelector('[data-dv-cart-stale]')) dvSyncProtection();
